import { Button, Col, Field, Input, Row, Select, Text } from "@rally/ui-kit";
import { useState } from "react";
import { say } from "../localization/say.ts";
import { act } from "../net/rally.ts";

export type Place = "rally-machine" | "external-managed" | "external-registered";

export type EnvironmentDraft = {
  name: string;
  place: Place;
  branch: string | null;
  machine: string | null;
  provider: string | null;
  url: string | null;
  healthPath: string | null;
  recipe: { install: string | null; build: string | null; start: string | null };
};

const PLACES: Place[] = ["rally-machine", "external-managed", "external-registered"];

export function AppEnvironmentForm({
  app,
  held,
  onDone,
  onCancel,
}: {
  app: string;
  held?: EnvironmentDraft | undefined;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(held?.name ?? "");
  const [place, setPlace] = useState<Place>(held?.place ?? "external-registered");
  const [branch, setBranch] = useState(held?.branch ?? "");
  const [machine, setMachine] = useState(held?.machine ?? "");
  const [provider, setProvider] = useState(held?.provider ?? "");
  const [url, setUrl] = useState(held?.url ?? "");
  const [healthPath, setHealthPath] = useState(held?.healthPath ?? "");
  const [install, setInstall] = useState(held?.recipe.install ?? "");
  const [build, setBuild] = useState(held?.recipe.build ?? "");
  const [start, setStart] = useState(held?.recipe.start ?? "");
  const [saving, setSaving] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);

  const save = () => {
    setSaving(true);
    setRefused(null);
    void act("app.setEnvironment", {
      app,
      name: name.trim(),
      place,
      branch: branch.trim(),
      machine: machine.trim(),
      provider: provider.trim(),
      url: url.trim(),
      healthPath: healthPath.trim(),
      install: install.trim() || null,
      build: build.trim() || null,
      start: start.trim() || null,
    })
      .then(() => {
        setSaving(false);
        onDone();
      })
      .catch((cause: unknown) => {
        setSaving(false);
        setRefused(cause instanceof Error ? cause.message : say("app.saveFailed"));
      });
  };

  return (
    <Col gap={3} testID="app-environment-form">
      <Field label={say("app.environmentName")}>
        <Input
          value={name}
          onChangeText={setName}
          placeholder="dev"
          editable={held === undefined}
          testID="environment-name"
        />
      </Field>
      <Field label={say("app.place")}>
        <Select
          value={place}
          onChange={(one) => setPlace(one as Place)}
          accessibilityLabel={say("app.place")}
          options={PLACES.map((one) => ({
            value: one,
            label: say(`app.place.${one}` as "app.place.rally-machine"),
          }))}
        />
      </Field>
      {place === "rally-machine" ? (
        <Col gap={3}>
          <Field label={say("app.machine")}>
            <Input value={machine} onChangeText={setMachine} testID="environment-machine" />
          </Field>
          <Field label={say("app.installCommand")} hint={say("app.installHint")}>
            <Input
              value={install}
              onChangeText={setInstall}
              placeholder="bun install"
              multiline={false}
              testID="environment-install"
            />
          </Field>
          <Field label={say("app.buildCommand")} hint={say("app.buildHint")}>
            <Input
              value={build}
              onChangeText={setBuild}
              placeholder="bun run build"
              multiline={false}
              testID="environment-build"
            />
          </Field>
          <Field label={say("app.startCommand")} hint={say("app.startHint")}>
            <Input
              value={start}
              onChangeText={setStart}
              placeholder="bun run start"
              multiline={false}
              testID="environment-start"
            />
          </Field>
        </Col>
      ) : null}
      {place === "external-managed" ? (
        <Field label={say("app.provider")}>
          <Input
            value={provider}
            onChangeText={setProvider}
            placeholder="vercel"
            testID="environment-provider"
          />
        </Field>
      ) : null}
      <Field label={say("app.branch")}>
        <Input value={branch} onChangeText={setBranch} placeholder="main" testID="environment-branch" />
      </Field>
      <Field label={say("app.address")}>
        <Input value={url} onChangeText={setUrl} placeholder="https://example.com" testID="environment-url" />
      </Field>
      <Field label={say("app.healthPath")}>
        <Input
          value={healthPath}
          onChangeText={setHealthPath}
          placeholder="/health"
          testID="environment-health"
        />
      </Field>
      {refused ? (
        <Text typeName="caption" color="danger">
          {refused}
        </Text>
      ) : null}
      <Row gap={2}>
        <Button onPress={save} disabled={saving || name.trim() === ""} testID="environment-save">
          {say("app.save")}
        </Button>
        <Button variant="ghost" onPress={onCancel} testID="environment-cancel">
          {say("app.cancel")}
        </Button>
      </Row>
    </Col>
  );
}
